import { useEffect } from 'react';
import type { DocumentStore } from '@burfy/docs-core';
import { IndexedDbDocumentStore } from './indexeddb-document-store.ts';
import { anotherTabIsOpen, announcePresence } from './other-tabs.ts';
import { assetStore } from './shared.ts';
import { sweepAssets } from './sweep-assets.ts';

/**
 * Once per page load, never again.
 *
 * The sweep is only safe at start-up: a fresh session has no undo history, so
 * nothing on screen can reach back to an asset that is unreferenced now. A
 * module-level flag rather than a ref, because StrictMode mounts, unmounts and
 * remounts in development and a per-component guard would let it run twice.
 */
let swept = false;

export function useAssetSweep(documents: DocumentStore): void {
  useEffect(() => {
    // Answer other tabs for as long as this one is open, whether or not this
    // tab ends up sweeping.
    const stopAnnouncing = announcePresence();

    if (swept) return stopAnnouncing;

    // Only the IndexedDB implementation has raw records to read; a test's
    // in-memory store has no orphans to collect either.
    if (!(documents instanceof IndexedDbDocumentStore)) return stopAnnouncing;

    /**
     * Both stores must be the same database.
     *
     * The document store comes from context and can be pointed elsewhere —
     * which is how the storage tests isolate themselves — while the asset
     * store here is the app's own. Pairing documents in one database against
     * assets in another finds every asset unreferenced and deletes all of
     * them.
     */
    if (documents.databaseName !== assetStore.databaseName) return stopAnnouncing;

    swept = true;

    void anotherTabIsOpen().then((busy) => {
      // Undo history is per tab: another tab may still be able to undo into an
      // asset that looks unreferenced from here.
      if (busy) return;

      // Nothing acts on the result: a sweep that reclaims nothing is the
      // normal case, and the only outcome worth saying anything about —
      // declining to delete because the references could not be read — warns
      // from inside.
      void sweepAssets(documents, assetStore);
    });

    return stopAnnouncing;
  }, [documents]);
}
